import type { NFTLayerAsset } from '../types';

/**
 * Single source of truth for NFT layer classification.
 *
 * Layered Nub NFTs expose per-trait assets in `properties.files` of the
 * off-chain metadata (`nftFetcher::enrichWithUriMetadata`). Background and
 * frame layers are fill layers: they always cover the full canvas and never
 * follow the character's pan/zoom. Everything else is a character layer that
 * renders at the element's position/scale/rotation.
 *
 * A layer is a fill layer when EITHER its id OR its type names it as a
 * background/frame (case-insensitive). Both fields must be checked: some
 * metadata uses generic ids with descriptive types and vice versa.
 */
export const isBackgroundLayer = (layer: NFTLayerAsset): boolean => {
  const idLower = layer.id.toLowerCase();
  const typeLower = (layer.type || '').toLowerCase();
  return idLower.includes('background') || typeLower.includes('background');
};

export const isFrameLayer = (layer: NFTLayerAsset): boolean => {
  const idLower = layer.id.toLowerCase();
  const typeLower = (layer.type || '').toLowerCase();
  return idLower.includes('frame') || typeLower.includes('frame');
};

/** Fill layers (background + frame) — fixed full-canvas layers. */
export const isFillLayerByDefault = (layer: NFTLayerAsset): boolean =>
  isBackgroundLayer(layer) || isFrameLayer(layer);

/** Character layers — everything that is not a fill layer. */
export const isCharacterLayer = (layer: NFTLayerAsset): boolean =>
  !isFillLayerByDefault(layer);
